import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  notification,
  Table,
  Drawer,
  Space,
  Spin,
  Card,
  Grid
} from 'antd';
  import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { DeleteOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;


const QuotationDetails = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const id = location.state?.quotationId;
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notesDrawerVisible, setNotesDrawerVisible] = useState(false);
  const [materialsDrawerVisible, setMaterialsDrawerVisible] = useState(false);
  const [notesData, setNotesData] = useState([]);
  const [materialsData, setMaterialsData] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  
  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  // Fetch dropdown data (clients/packages)
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [clientsRes, packagesRes] = await Promise.all([
          axios.get(`${baseUrl}/clients`),
          axios.get(`${baseUrl}/packages`),
        ]);
        setClients(clientsRes.data);
        setPackages(packagesRes.data);
      } catch (error) {
        notification.error({ message: 'Failed to fetch client/package data' });
      }
    };
    fetchDropdownData();
  }, [baseUrl]);

  // Fetch quotation details if editing
  useEffect(() => {
    if (!id) return;
    const fetchQuotationDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${baseUrl}/quotations/${id}`);
        const data = res.data;
        // setSelectedPackageId(data.PackageID);
        form.setFieldsValue({
          ...data,
          ValidFromDate: data.ValidFromDate ? dayjs(data.ValidFromDate) : null,
          ValidToDate: data.ValidToDate ? dayjs(data.ValidToDate) : null,
        });
        // Parse Notes
        try {
          let parsedNotes = JSON.parse(data.Notes);
          if (typeof parsedNotes === 'string') parsedNotes = JSON.parse(parsedNotes);
          setNotesData(Array.isArray(parsedNotes) ? parsedNotes : []);
        } catch {
          setNotesData([]);
        }
        // Parse Materials
        try {
          let parsedMaterials = JSON.parse(data.Materials);
          if (typeof parsedMaterials === 'string') parsedMaterials = JSON.parse(parsedMaterials);
          setMaterialsData(Array.isArray(parsedMaterials) && parsedMaterials.length > 0 ? parsedMaterials : DEFAULT_MATERIALS);
        } catch {
          setMaterialsData(DEFAULT_MATERIALS);
        }
      } catch (error) {
        notification.error({
          message: 'Failed to fetch quotation details',
          description: 'Please check the quotation ID or try again later.'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchQuotationDetails();
  }, [id, baseUrl, form]);

  // Fetch notes/materials for selected package
  const fetchPackageNotes = async (pkgId) => {
    try {
      const res = await axios.get(`${baseUrl}/package-notes/${pkgId}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setNotesData(res.data.map(note => ({ name: note.content, value: '', type: 'notes' })));
      }
    } catch {
      notification.error({ message: 'Failed to fetch package notes' });
    }
  };
  const fetchPackageMaterials = async (pkgId) => {
    try {
      const res = await axios.get(`${baseUrl}/package-materials/${pkgId}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setMaterialsData(res.data.map(material => ({ name: material.name, value: '', type: 'materials' })));
      }
    } catch {
      notification.error({ message: 'Failed to fetch package materials' });
    }
  };

  // Handler for package selection
  const handlePackageChange = async (value) => {
    setSelectedPackageId(value);
    if (value) {
      await fetchPackageNotes(value);
      await fetchPackageMaterials(value);
    }
  };

  // Form submit handler
  const onFinish = async (values) => {
    setLoading(true);
    try {
      values.ValidFromDate = values.ValidFromDate ? values.ValidFromDate.format('YYYY-MM-DD') : null;
      values.ValidToDate = values.ValidToDate ? values.ValidToDate.format('YYYY-MM-DD') : null;
      values.Notes = JSON.stringify(notesData);
      values.Materials = JSON.stringify(materialsData);
      if (id) {
        await axios.put(`${baseUrl}/quotations/${id}`, values);
        notification.success({ message: 'Quotation Updated', description: 'Quotation details have been successfully updated.' });
      } else {
        const response = await axios.post(`${baseUrl}/quotations`, values);
         
        const copyCompoenents = await axios.get(`${baseUrl}/quotations/${selectedPackageId}/copy/${response.data.QuotationID}`);
        notification.success({ message: 'Quotation Created', description: 'Quotation has been created.' });
      }
    } catch (error) {
      notification.error({ message: 'Failed to save quotation' });
    } finally {
      setLoading(false);
    }
  };

  // Table columns for notes/materials
  const tableColumns = [
    {
      title: 'SR.',
      dataIndex: 'srNo',
      key: 'srNo',
      render: (_, __, index) => index + 1,
      width: '8%',
    },
    {
      title: 'Description',
      dataIndex: 'name',
      width: '50%',
      render: (text, record, index) => (
        <Input.TextArea
          value={text}
          onChange={e => {
            const setter = record.type === 'notes' ? setNotesData : setMaterialsData;
            const data = record.type === 'notes' ? [...notesData] : [...materialsData];
            data[index].name = e.target.value;
            setter(data);
          }}
        />
      ),
    },
    {
      title: 'Price',
      dataIndex: 'value',
      width: '25%',
      render: (text, record, index) => (
        <Input.TextArea
          value={text}
          onChange={e => {
            const setter = record.type === 'notes' ? setNotesData : setMaterialsData;
            const data = record.type === 'notes' ? [...notesData] : [...materialsData];
            data[index].value = e.target.value;
            setter(data);
          }}
        />
      ),
    },
    {
      title: 'Delete',
      width: '5%',
      render: (_, record, index) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          style={{ marginLeft: 8 }}
          onClick={() => {
            const setter = record.type === 'notes' ? setNotesData : setMaterialsData;
            const data = record.type === 'notes' ? [...notesData] : [...materialsData];
            data.splice(index, 1);
            setter(data);
          }}
        />
      ),
    },
  ];

  return (
    <Card bordered={false} style={{ boxShadow: 'none', borderRadius: 8 }} bodyStyle={{ padding: '16px 24px' }}>
      <Row style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>{id ? 'Edit Quotation' : 'Create Quotation'}</Title>
          <Text type="secondary">{id ? 'Edit the details of the quotation' : 'Fill in the details to create a new quotation'}</Text>
        </Col>
      </Row>
      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ Notes: '{}', Materials: '{}' }}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item name="UserID" label="Client" rules={[{ required: true, message: 'Please select a client' }]}> 
              <Select placeholder="Select Client">
                {clients.map(client => (
                  <Option key={client.ClientID} value={client.ClientID}>{client.Name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="PackageID" label="Package" rules={[{ required: true, message: 'Please select a package' }]}> 
              <Select placeholder="Select Package" onChange={handlePackageChange} >
                {packages.map(pkg => (
                  <Option key={pkg.PackageID} value={pkg.PackageID}>{pkg.PackageName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="ProjectName" label="Project Name" rules={[{ required: true, message: 'Please enter a project name' }]}> 
              <Input placeholder="Project Name" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item name="Area" label="Area (Sq. Ft.)" initialValue={0} rules={[{ required: true, message: 'Please enter an area' }]}> 
              <InputNumber style={{ width: '100%' }} placeholder="Area" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="ValidFromDate" label="Valid From Date" rules={[{ required: true, message: 'Please select a valid from date' }]}> 
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="ValidToDate" label="Valid To Date" rules={[{ required: true, message: 'Please select a valid to date' }]}> 
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item name="SalesPersonName" label="Sales Person Name" rules={[{ required: true, message: 'Please enter a sales person name' }]}> 
              <Input placeholder="Sales Person Name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="SalesPersonMobile" label="Sales Person Mobile" rules={[{ required: true, message: 'Please enter a sales person mobile' }]}> 
              <Input placeholder="Sales Person Mobile" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12}>
            <Button type="default" onClick={() => setNotesDrawerVisible(true)} style={{ width: '100%' }}>
              Manage Notes ({notesData.length})
            </Button>
          </Col>
          <Col xs={24} sm={12}>
            <Button type="default" onClick={() => setMaterialsDrawerVisible(true)} style={{ width: '100%' }}>
              Manage Materials ({materialsData.length})
            </Button>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: screens.xs ? 'center' : 'left', marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>{id ? 'Update Quotation' : 'Create Quotation'}</Button>
              <Button onClick={() => navigate('/quotations')}>Cancel</Button>
            </Space>
          </Col>
        </Row>
        
      </Form>
      {loading && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
      )}
      <Drawer
        title="Manage Notes"
        placement="right"
        open={notesDrawerVisible}
        onClose={() => setNotesDrawerVisible(false)}
        width={800}
        extra={<Button type="dashed" onClick={() => setNotesData([...notesData, { name: '', value: '', type: 'notes' }])}>Add Note</Button>}
        bodyStyle={{ maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}
      >
        <Table
          dataSource={notesData.map(item => ({ ...item, type: 'notes' }))}
          columns={tableColumns}
          rowKey={(_, index) => index}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: 'No notes added' }}
          bordered
          style={{ minHeight: 200 }}
        />
      </Drawer>
      <Drawer
        title="Manage Materials"
        placement="right"
        open={materialsDrawerVisible}
        onClose={() => setMaterialsDrawerVisible(false)}
        width={800}
        extra={<Button type="dashed" onClick={() => setMaterialsData([...materialsData, { name: '', value: '', type: 'materials' }])}>Add Material</Button>}
        bodyStyle={{ maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}
      >
        <Table
          dataSource={materialsData.map(item => ({ ...item, type: 'materials' }))}
          columns={tableColumns}
          rowKey={(_, index) => index}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: 'No materials added' }}
          bordered
          style={{ minHeight: 200 }}
        />
      </Drawer>
    </Card>
  );
};

export default QuotationDetails;